import { forwardRef, useCallback, useEffect, useImperativeHandle, useReducer, useRef, useState } from 'react';
import { useIsRTL } from '../../locale';
import { createUseTextLocalizer } from '../../locale/createUseTextLocalizer';
import { renderInlineCssVars, useDesignTokens } from '../../theme';
import { postfixTestId } from '../../util/testId';
import { BaseTable, hasSelectionColumn } from '../Table/__internal__/BaseTable';
import { defaultSizes } from '../Table/__internal__/Defaults';
import { SelectionCellContent } from '../Table/__internal__/SelectionCellContent';
import { getTreeTokens } from '../Tree';
import { makeSayCell, makeSaySelectionCell } from './__internal__';
import { TreeTableNode } from './TreeTableNode';
import { TreeTableState } from './TreeTableState';
import bundle from './NovaComponents-TreeTable-gui-icu.properties';
import { jsx as _jsx } from "react/jsx-runtime";
export const DEFAULT_INDENTATION = 20;
const DEFAULT_ICON_OFFSET = 8;
const DEFAULT_ICON_WIDTH = 14;
const DEFAULT_ICON_MARGIN = 4;
const useTextLocalizer = createUseTextLocalizer(bundle);
export const createLabelId = (cellId, treeTableId) => {
  const nodeLabelPrefix = 'tree-node';
  if (treeTableId) {
    return `${nodeLabelPrefix}-${treeTableId}-${cellId}`;
  } else {
    return `${nodeLabelPrefix}-${cellId}`;
  }
};
const CellContent = /*#__PURE__*/forwardRef((cellProps, ref) => {
  const {
    hasChildren,
    id,
    level
  } = cellProps.node;
  const isExpanded = cellProps.expandedNodes?.has(id);
  const indent = level * (cellProps.indentSpacing ?? DEFAULT_INDENTATION) - (hasChildren ? level === 0 ? DEFAULT_ICON_OFFSET : DEFAULT_ICON_WIDTH + DEFAULT_ICON_MARGIN : level === 0 ? -(DEFAULT_ICON_WIDTH - DEFAULT_ICON_MARGIN) : 0);
  const itemData = cellProps.itemData;
  const rowIndex = cellProps.rowIndex;
  const selected = itemData?.preference?.selectAll;
  const hideUnselectedSelectionControl = itemData?.hideUnselectedSelectionControl;
  const [displayState, setDisplayState] = useState(hideUnselectedSelectionControl ? 'hidden' : 'visible');
  const focused = itemData?.lastFocusedCell?.rowIndex === rowIndex && itemData?.lastFocusedCell?.colIndex === 0 && itemData?.focusIndicator === 'visible';
  useEffect(() => {
    if (hideUnselectedSelectionControl) {
      let checkBoxState;
      if (itemData.draggingRow > -1 && itemData.dragging && rowIndex === itemData.draggingRow) checkBoxState = 'hidden';else if (selected || focused) checkBoxState = 'visible';else checkBoxState = 'hidden';
      setDisplayState(checkBoxState);
    }
  }, [selected, focused, hideUnselectedSelectionControl, rowIndex, itemData.draggingRow, itemData.dragging]);
  useEffect(() => {
    if (itemData.hideUnselectedSelectionControl === false) setDisplayState('visible');
  }, [itemData.hideUnselectedSelectionControl]);
  const getSelectionControl = labelledBy => {
    if (itemData && itemData?.hideSelectionControlColumn === true && (itemData.selectionMode === 'parentChild' || itemData.selectionMode === 'multiple')) return /*#__PURE__*/_jsx(SelectionCellContent, {
      rowIndex: cellProps.rowIndex,
      itemData: itemData,
      style: {
        visibility: displayState
      },
      ariaLabelledBy: labelledBy
    });
    return undefined;
  };
  const treeNodeContent = /*#__PURE__*/_jsx("div", {
    style: {
      marginInlineStart: `${indent}px`
    },
    onMouseOver: () => {
      hideUnselectedSelectionControl && setDisplayState('visible');
    },
    onMouseLeave: () => {
      hideUnselectedSelectionControl && setDisplayState(selected || focused ? 'visible' : 'hidden');
    },
    children: /*#__PURE__*/_jsx(TreeTableNode, {
      testId: cellProps.testId,
      ref: ref,
      expanded: isExpanded,
      node: cellProps.node,
      hasChildren: hasChildren,
      onArrowClick: () => {
        cellProps.onExpand?.(cellProps.node, !isExpanded);
      },
      suppressTooltip: cellProps.suppressTooltip,
      disabled: cellProps.disabled,
      expandCollapseDisabled: cellProps.isExpandCollapseDisabled?.(cellProps.node, cellProps.rowKey),
      nodeElement: cellProps.nodeElement,
      selectionControl: getSelectionControl(createLabelId(cellProps.node.id, cellProps.treeTableId)),
      labelId: createLabelId(cellProps.node.id, cellProps.treeTableId)
    })
  });
  return cellProps.getNodeContent?.(cellProps.rowIndex, treeNodeContent) ?? treeNodeContent;
});
export const TreeTable = ({
  indentSpacing = DEFAULT_INDENTATION,
  ...props
}) => {
  const currentPosition = useRef();
  const isRTL = useIsRTL();
  const narratorRef = useRef();
  const currentHoveredRowRef = useRef();
  const previousValidHover = useRef(true);
  const localize = useTextLocalizer();
  const dt = useDesignTokens();
  const tokens = renderInlineCssVars(getTreeTokens(dt), 'tree');
  const refGrid = useRef(null);
  const [dropLevel, currentDropLevel] = useState(-1);
  const [treeState, dispatch] = useReducer(TreeTableState.reducer, TreeTableState.init(props.preference?.selectedRows, props.preference?.partiallySelectedRows));
  const [interactiveSelection, setInteractiveSelection] = useState(false);
  const [interactiveSelectAll, setInteractiveSelectAll] = useState(false);
  const [initialSelectAll, setInitialSelectAll] = useState(false);
  const updateSelection = useCallback((selectedRows, selectAll, lastSelectedRowKey, selected, interactive) => {
    if (props.readOnly && interactive) return;
    if ((!selectedRows || selectedRows.size === 0) && selectAll !== undefined && selectAll !== 'mixed' && !props.hideSelectionControlColumn) {
      props.setSelectAllRows?.({
        selectAll,
        isRowReadOnly
      });
    } else if (props.selectionMode === 'parentChild') {
      props.setSelectRows?.({
        selectedRows,
        rowCount: props.rowCount,
        interactive,
        isRowDisabled,
        isRowReadOnly,
        areRowsVisible: props.areRowsVisible
      });
    }
    interactive && props.onSelect?.(selectedRows, selectAll, lastSelectedRowKey, selected);
    setInteractiveSelection(false);
  },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [treeState.selectedRows]);
  useEffect(() => {
    const setPartiallySelectedRows = props.setPartiallySelectedRows;
    setPartiallySelectedRows?.(treeState.partiallySelectedRows);
  }, [props.setPartiallySelectedRows, treeState.partiallySelectedRows]);
  useEffect(() => {
    updateSelection(treeState.selectedRows, props.preference?.selectAll, props.preference?.selectionMetadata?.key, props.preference?.selectionMetadata?.state, interactiveSelection);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [treeState.selectedRows, treeState.selectAll]);
  useEffect(() => {
    if (!interactiveSelectAll && treeState.selectedRows?.size === 0 && (treeState.selectAll === false || treeState.selectAll === true)) {
      props.setSelectAllRows?.({
        selectAll: treeState.selectAll,
        isRowReadOnly
      });
    }
    setInteractiveSelectAll(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [treeState.selectAll]);
  useImperativeHandle(props.treeTableAPIRef, () => ({
    setSelectedRows: rowKeys => {
      if (props.selectionMode === 'parentChild') {
        dispatch({
          type: 'parentChildSelection',
          rowKey: rowKeys?.length > 0 ? rowKeys[0] : '',
          selectedRows: new Set(rowKeys),
          selected: true,
          props,
          interactive: false,
          selectAll: treeState.selectAll
        });
      } else if (props.selectionMode !== 'none') {
        const keys = props.selectionMode === 'single' ? new Set([rowKeys?.length > 0 ? rowKeys[0] : '']) : new Set(rowKeys);
        props.setSelectRows?.({
          selectedRows: keys,
          isRowReadOnly
        });
      }
    },
    setSelectAll: selectAll => {
      setInitialSelectAll(selectAll);
      if (props.selectionMode === 'parentChild') {
        dispatch({
          type: 'parentChildSelection',
          rowKey: '',
          selectedRows: new Set([]),
          selectAll,
          props,
          interactive: false
        });
      } else {
        dispatch({
          type: 'selectRow',
          selectAll
        });
      }
    },
    getColumnIndexForOffset: scrollLeft => {
      return refGrid.current?.getColumnIndexForOffset(scrollLeft);
    },
    getRowIndexForOffset(scrollTop) {
      return refGrid.current?.getRowIndexForOffset(scrollTop);
    }
  }));
  const treeTableSayCell = makeSayCell(localize, props.selectionMode, props.getDataNode, props.expandedNodes, props.getRowAttributes, props.getRowKey, props.hideSelectionControlColumn, props.preference?.partiallySelectedRows, props.preference?.selectAll, props.preference?.selectedRows);
  const treeTableSaySelectionCell = makeSaySelectionCell(localize, props.getDataNode, props.getRowAttributes, props.getRowKey);
  useEffect(() => {
    const setFreezeColumnCount = props.setFreezeColumnCount;
    setFreezeColumnCount?.({
      count: props.frozenColumnCount ?? 0
    });
  }, [props.frozenColumnCount, props.setFreezeColumnCount]);
  useEffect(() => {
    narratorRef.current?.announcePosition();
  }, [props.expandedNodes]);
  const selectionColumnIndex = props.disabled || props.hideSelectionControlColumn || props.selectionMode !== 'multiple' && props.selectionMode !== 'parentChild' ? 0 : 1;
  const moveDropIndicator = params => {
    const {
      rowIndex,
      dropIndicatorRef,
      offsetX,
      height,
      width,
      position
    } = params;

    // Early return if any of the necessary parameters is missing
    if (!dropIndicatorRef?.current || !width || !offsetX) return;
    const node = props?.getDataNode?.(rowIndex);
    const dropUnderNode = props?.getDataNode?.(rowIndex - 1);
    const nodeLevel = (node?.level ?? 0) || 1;
    const indentation = params.indentSpacing ?? DEFAULT_INDENTATION;
    const updatedOffset = currentHoveredRowRef.current !== rowIndex && previousValidHover.current ? (nodeLevel + 1) * indentation : isRTL ? offsetX > 0 ? 0 : Math.abs(offsetX) : Math.max(0, offsetX);
    const dropLevel = Math.max(0, Math.floor(updatedOffset / indentation * nodeLevel));
    const valid = props.isValidDropLevel ? props.isValidDropLevel(dropUnderNode, dropLevel) : true;
    previousValidHover.current = valid;
    if (!valid) {
      dropIndicatorRef.current.style.top = `${0}px`;
      dropIndicatorRef.current.style.display = 'none';
      return;
    }
    currentHoveredRowRef.current = rowIndex;
    const showSelectionControl = hasSelectionColumn(props.selectionMode === 'parentChild' ? 'multiple' : props.selectionMode, props.columns?.length || 0, props.rowCount, props.hideSelectionControlColumn);
    const indent = dropLevel * indentation + (showSelectionControl ? defaultSizes.selectionControlColumnWidth : 0);
    currentDropLevel(isRTL ? offsetX > 0 ? 0 : dropLevel : offsetX < 0 ? 0 : dropLevel);
    dropIndicatorRef.current.style.top = `${position}px`;
    dropIndicatorRef.current.style[isRTL ? 'right' : 'left'] = `${indent}px`;
    dropIndicatorRef.current.style.width = `${width - indent}px`;
    dropIndicatorRef.current.style.height = `${height ?? defaultSizes.dropIndicatorLineHeight}px`;
    dropIndicatorRef.current.style.display = position ?? -1 > -1 ? 'block' : 'none';
  };
  const isRowDisabled = key => {
    if (props.isRowDisabled) return props.isRowDisabled(key);
    return props.disabledRows?.has(key) || false;
  };
  const isRowReadOnly = key => {
    if (props.isRowReadOnly) return props.isRowReadOnly(key);
    return props.readonlyRows?.has(key) || false;
  };
  const areSetsEqual = (set1, set2) => {
    if (set1.size !== set2.size) return false;
    for (const item of set1) {
      if (!set2.has(item)) return false;
    }
    return true;
  };
  return /*#__PURE__*/_jsx("div", {
    style: tokens,
    onKeyDown: event => {
      if (event.key === 'Enter' && event.ctrlKey) {
        if (currentPosition.current) {
          const node = props.getDataNode ? props.getDataNode(currentPosition.current.rowIndex) : undefined;
          if (node && (props.isExpandCollapseDisabled?.(node, props.getRowKey?.(currentPosition.current.rowIndex)) || props.disabledRows?.has(props.getRowKey?.(currentPosition.current.rowIndex) || '') || props.disabled)) return;
          const treeColIndex = selectionColumnIndex;
          if (node && currentPosition.current.colIndex === treeColIndex) {
            const expanded = props.expandedNodes.has(node.id);
            props.onExpand?.(node, !expanded);
          }
        }
      }
    },
    children: /*#__PURE__*/_jsx(BaseTable, {
      ...props,
      columns: props.columns ? props.columns.length > 1 ? [{
        ...props.columns[0],
        rowHeaderColumn: true
      }, ...props.columns.slice(1)] : props.columns : undefined,
      internalRef: refGrid,
      disableOnDoubleClickKeyboardActivation: true,
      ariaDescribedBy: props.ariaDescribedBy,
      addRowAttributes: rowIndex => {
        const node = props.getDataNode?.(rowIndex);
        const rowKey = props.getRowKey?.(rowIndex);
        if (!node || !rowKey) return {};
        const rowAttributes = props.getRowAttributes?.(rowKey);
        return {
          ...(rowAttributes?.level && {
            'aria-level': rowAttributes.level
          }),
          'aria-rowindex': rowIndex + 1
        };
      },
      moveDropIndicator: props.allowLevelDrop ? params => {
        moveDropIndicator(params);
      } : undefined,
      getDropLevel: () => {
        return props.allowLevelDrop ? dropLevel : -1;
      },
      addCellAttributes: (rowIndex, columnIndex) => {
        if (rowIndex > -1) {
          // If selectionMode of undefined is passed to the BaseTable it defaults to 'multiple'
          const hasSelectionColumn = props.selectionMode === 'parentChild' || props.selectionMode === 'multiple' || props.selectionMode === undefined;
          const node = props.getDataNode?.(rowIndex);
          const firstColumn = hasSelectionColumn ? 1 : 0;
          if (columnIndex === firstColumn && node) {
            const isExpanded = node.hasChildren ? props.expandedNodes?.has(node.id) : undefined;
            return {
              'aria-expanded': isExpanded
            };
          }
        }
        return {};
      },
      role: props.role ?? 'treegrid',
      narratorRef: narratorRef,
      setSelectRow: selectionProps => {
        if (props.readOnly) return;
        const {
          setSelectRow,
          rowCount,
          hideSelectAll,
          hideSelectionControlColumn,
          selectionMode,
          columnHeadersVisible,
          areRowsVisible
        } = props;
        const hasNoSelectAll = selectionMode ? ['single', 'none', 'desktop'].includes(selectionMode) : false;
        const isParentChild = props.selectionMode === 'parentChild';
        const selectedRowProps = {
          ...selectionProps,
          clearPrevious: isParentChild ? false : selectionProps.clearPrevious,
          rowCount,
          isRowDisabled,
          isRowReadOnly,
          hideSelectAll: hideSelectAll || hideSelectionControlColumn || hasNoSelectAll && !columnHeadersVisible,
          columnHeadersVisible,
          areRowsVisible,
          interactive: true
        };
        setSelectRow?.(selectedRowProps);
      },
      setSelectAllRows: selectionProps => {
        if (props.readOnly) return;
        props.setSelectAllRows?.(selectionProps);
      },
      selectionMode: props.selectionMode === 'parentChild' ? 'multiple' : props.selectionMode,
      selectAll: props.selectAll,
      getSelectAllCheckboxState: () => {
        const {
          getSelectAllCheckboxState,
          disabledRows,
          readonlyRows,
          selectionMode,
          disabledAndSelectedRows,
          readOnlyAndSelectedRows,
          preference
        } = props;
        const state = getSelectAllCheckboxState?.();
        const disabledRowsSize = disabledRows?.size ?? 0;
        const readonlyRowsSize = readonlyRows?.size ?? 0;
        const selectedRows = treeState.selectedRows;
        const selectedRowsSize = selectedRows?.size ?? 0;
        const disabledAndSelectedRowsSize = disabledAndSelectedRows?.size ?? 0;
        const readOnlyAndSelectedRowsSize = readOnlyAndSelectedRows?.size ?? 0;
        if (disabledRowsSize === 0 && readonlyRowsSize === 0) {
          if (state !== undefined || selectionMode !== 'parentChild' || !interactiveSelection) return state;
        }
        if (selectedRows && disabledRowsSize > 0) {
          const combined = new Set([...selectedRows, ...(readOnlyAndSelectedRows || []), ...(disabledRows || [])]);
          const areRowsSelectedRowsVisible = props.areRowsVisible ? props.areRowsVisible(combined) : true;
          if (areRowsSelectedRowsVisible && combined.size >= props.rowCount || treeState.selectAll === true) return true;
          if (readOnlyAndSelectedRowsSize > 0) return 'mixed';
          if (!selectedRows || selectedRowsSize === 0 || disabledAndSelectedRows && areSetsEqual(selectedRows, disabledAndSelectedRows) || disabledAndSelectedRowsSize > selectedRowsSize) {
            return false;
          }
          if (selectedRowsSize > 0) return 'mixed';
        }
        if (treeState.selectAll !== true && selectedRows && (selectedRows?.size ?? 0) > 0) {
          const combined = new Set([...selectedRows, ...(disabledRows || [])]);
          if (combined.size >= props.rowCount) return true;
          return 'mixed';
        }
        if (treeState.selectAll === true && readonlyRowsSize > (props.readOnlyAndSelectedRows?.size ?? 0)) return 'mixed';
        return treeState.selectAll || preference?.selectAll;
      },
      getSelectionCheckboxState: rowKey => {
        const state = props.getSelectionCheckboxState?.(rowKey);
        if (state !== undefined) return state;
        if (props.disabledRows?.size ?? 0 > 0) {
          if (props.disabledAndSelectedRows?.has(rowKey)) return true;
          if (props.disabledRows?.has(rowKey)) return false;
        }
        if (isRowReadOnly?.(rowKey)) {
          if (props.readOnlyAndSelectedRows?.has(rowKey) || initialSelectAll === true) return true;else if (treeState.partiallySelectedRows?.includes(rowKey)) return 'mixed';else return false;
        }
        const contains = treeState.partiallySelectedRows?.includes(rowKey);
        return contains === true ? 'mixed' : undefined;
      },
      getSelectionState: rowKey => {
        const state = props.getSelectionCheckboxState?.(rowKey);
        if (state !== undefined) return state;
        if (props.disabledRows?.size ?? 0 > 0) {
          if (props.disabledAndSelectedRows?.has(rowKey)) return true;
          if (props.disabledRows?.has(rowKey)) return false;
        }
        if (isRowReadOnly?.(rowKey) && props.readOnlyAndSelectedRows?.has(rowKey)) return true;else if (isRowReadOnly?.(rowKey)) return false;
        return undefined;
      },
      unmanagedColumnKeys: props.columns && props.columns[0] ? [props.columns[0].key] : undefined,
      renderCell: ({
        rowIndex,
        columnKey,
        columnIndex,
        disabled,
        ref,
        itemData
      }) => {
        const node = props.getDataNode?.(rowIndex);
        if (columnIndex === 0 && node) {
          const rowKey = props.getRowKey?.(rowIndex);
          return /*#__PURE__*/_jsx(CellContent, {
            testId: postfixTestId(props.testId, 'cellContent'),
            indentSpacing: indentSpacing,
            treeTableId: props.id,
            ref: ref,
            rowIndex: rowIndex,
            rowKey: rowKey,
            node: node,
            isExpandCollapseDisabled: props.isExpandCollapseDisabled,
            expandedNodes: props.expandedNodes,
            onExpand: props.onExpand,
            disabled: disabled,
            getNodeContent: props.getDataNodeContainer,
            nodeElement: props.getNodeElement?.(rowKey, ref),
            itemData: itemData,
            suppressTooltip: props.suppressNodeTooltip
          });
        }
        return props.renderCellData({
          columnIndex,
          rowIndex,
          columnKey,
          disabled
        });
      },
      onSelect: (selectedRows, selectAll, lastSelectedRowKey, lastSelectedRowState) => {
        setInteractiveSelection(true);
        if (props.selectionMode !== 'parentChild') {
          dispatch({
            type: 'selectRow',
            selectedRows,
            selectAll,
            interactive: true
          });
        } else {
          if (selectAll === true || selectAll === false) setInteractiveSelectAll(true);
          dispatch({
            type: 'parentChildSelection',
            rowKey: lastSelectedRowKey || '',
            selectedRows,
            selectAll,
            selected: lastSelectedRowState,
            props,
            interactive: true
          });
        }
      },
      onFocusChange: position => {
        currentPosition.current = position;
        props.onFocusChange?.(position);
      },
      narration: {
        onSayCell: treeTableSayCell,
        onSaySelectionCell: treeTableSaySelectionCell,
        disableRowChangeNarration: true,
        disableRowSelectionNarration: true
      }
    })
  });
};
//# sourceMappingURL=TreeTable.js.map